import { ORGANIZATIONS_KEY, organizationsAtom } from '@/atoms/organizations.atom';
import { buildOrgUrl, getOrgSlugFromHostname, isAppDomain } from '@/core/domain';
import { execute } from '@/graphql/execute';
import { MyOrganizationsQuery, UserOrganizationRole } from '@/graphql/graphql';
import { myOrganizationsQuery } from '@/queries/organization.query';
import { useAtom } from 'jotai';
import { useEffect, useMemo, useState } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';

const roleToLevel: Record<UserOrganizationRole, number> = {
  ADMIN: 3,
  EDITOR: 2,
  MEMBER: 1,
};

export const useOrganization = () => {
  const [organizations] = useAtom(organizationsAtom);
  const slug = getOrgSlugFromHostname();
  return organizations.find((organization) => organization.slug === slug);
};

export const useOrganizations = () => {
  const { pathname } = useLocation();
  const slug = getOrgSlugFromHostname();
  const [organizations, setOrganizations] = useAtom(organizationsAtom);
  const [loaded, setLoaded] = useState(false);

  const navigate = useNavigate();

  useEffect(() => {
    if (!loaded) return;
    if (pathname.startsWith('/shared')) return;
    if (organizations.length === 0) {
      navigate('/auth/onboarding/org');
      return;
    }
    // On app domain with no org slug, redirect to first org's subdomain
    if (isAppDomain() && !pathname.startsWith('/auth')) {
      window.location.href = buildOrgUrl(organizations[0].slug!);
    }
  }, [slug, organizations, loaded]);

  const _handleLoadOrganizations = async () => {
    const data = await execute(myOrganizationsQuery);
    const organizations = data.organizations || [];
    setOrganizations(organizations as MyOrganizationsQuery['organizations']);
    localStorage.setItem(ORGANIZATIONS_KEY, JSON.stringify(organizations));
    setLoaded(true);
  };

  useEffect(() => {
    _handleLoadOrganizations();
  }, []);

  const _handleOrganizationChange = (orgSlug: string) => {
    if (orgSlug === slug) return;
    window.location.href = buildOrgUrl(orgSlug);
  };

  const organization = useMemo(() => {
    if (!slug) return undefined;
    const org = organizations.find((organization) => organization.slug === slug);
    if (!org && organizations.length > 0 && loaded) {
      // Current slug doesn't match any org, redirect to first org
      window.location.href = buildOrgUrl(organizations[0].slug!);
    }
    return org;
  }, [organizations, slug, loaded]);

  const _checkOrganizationRole = (role: UserOrganizationRole) => {
    return roleToLevel[organization?.role || 'MEMBER'] >= roleToLevel[role as UserOrganizationRole];
  };

  return {
    organizations,
    setOrganizations,
    refreshOrganizations: _handleLoadOrganizations,
    organizationChange: _handleOrganizationChange,
    hasRole: _checkOrganizationRole,
    organization,
  };
};
